import { HStack } from '@chakra-ui/react';
import {
    ArrowDownToLine,
    Circle,
    Diamond,
    Equal,
    GitMerge,
    MapPin,
    MoveHorizontal,
    Square,
    Tangent,
    Target,
    Triangle,
    Waves,
    X,
    XCircle,
} from 'lucide-react';
// Direct import (not the snapping barrel): the barrel re-exports the
// drei-based SnapIndicator, which must not land in the StatusBar chunk.
import { useSnapStore, type SnapType } from '../../tools/snapping/SnapManager';
import { IconToggle } from './IconToggle';
import { useTranslation } from 'react-i18next';

/** Status-bar order mirrors the AutoCAD OSNAP bar: endpoint first, parallel
 *  last. (No 'grid' entry — grid stepping is F9, not an object snap.) The
 *  Faz 7.9 additions sit at their OCS priority slots. */
const SNAP_TYPE_ORDER: SnapType[] = [
    'endpoint',
    'midpoint',
    'center',
    'node',
    'quadrant',
    'intersection',
    'apparentIntersection',
    'extension',
    'insertion',
    'perpendicular',
    'tangent',
    'geometricCenter',
    'nearest',
    'parallel',
];

/** Lucide glyphs mirroring the AutoCAD OSNAP marker shapes (square,
 *  triangle, circle, crossed circle, ...), rendered through the shared
 *  status-bar `IconToggle` so they match the BIM workbench icons in
 *  size, spacing and chrome. */
const SNAP_TYPE_ICONS: Record<SnapType, typeof Square> = {
    endpoint: Square,
    midpoint: Triangle,
    center: Circle,
    node: XCircle,
    quadrant: Diamond,
    intersection: X,
    extension: MoveHorizontal,
    perpendicular: ArrowDownToLine,
    tangent: Tangent,
    nearest: Waves,
    parallel: Equal,
    geometricCenter: Target,
    insertion: MapPin,
    apparentIntersection: GitMerge,
};

/**
 * AutoCAD-style running object-snap switches: one glyph per snap type,
 * clickable to enable/disable that family. Rendered through the shared
 * status-bar `IconToggle` — the exact button style, icon size and
 * spacing of the BIM workbench status-bar icons (neutral gray glyphs,
 * no accent colors; when ON the background lightens via the shared
 * `bg.active` token). With the master OSNAP switch off the glyphs dim
 * while staying toggleable. The row scrolls internally (hidden
 * scrollbar) instead of wrapping or clipping when the window narrows.
 */
export function SnapTypeToggles() {
    const { t } = useTranslation();
    const types = useSnapStore((s) => s.types);
    const osnapEnabled = useSnapStore((s) => s.enabled);
    const toggleSnapType = useSnapStore((s) => s.toggleSnapType);

    return (
        <HStack
            spacing={1}
            minW={0}
            overflowX="auto"
            sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        >
            {SNAP_TYPE_ORDER.map((type) => {
                const on = osnapEnabled && types[type];
                return (
                    <IconToggle
                        key={type}
                        icon={SNAP_TYPE_ICONS[type]}
                        on={on}
                        dimmed={!osnapEnabled}
                        onClick={() => toggleSnapType(type)}
                        title={`${t(`statusbar.snapType.${type}`)} — ${types[type] ? t('statusbar.snapOn') : t('statusbar.snapOff')}`}
                    />
                );
            })}
        </HStack>
    );
}
